import { CJK_FONT_PRESETS, type DxfTextStyle } from '@drawing2d/core';
import { fill, t, type Lang } from '../i18n.js';

/**
 * Which font the DXF asks for, and who gets to decide.
 *
 * A DXF names a font; it cannot carry one. So whether the Japanese in a title
 * block appears at all is settled on the machine the file is opened on, and
 * **nothing this application writes can make a reader draw a glyph it does not
 * have.** That is a property of the format.
 *
 * That distinction is why this control exists rather than a cleverer default.
 * Naming MS Gothic is right for Japanese Windows, where these drawings are
 * issued, and useless on a Mac or in an online viewer that has no Japanese font
 * at all — which is exactly where the first report of `??` came from, on a file
 * whose characters were byte-perfect. The application does not know what is
 * installed where the file is going. The operator does.
 *
 * So the choice is offered, the consequence is stated in one sentence, and the
 * sentence says the part that is easy to leave out: if the reader has no Japanese
 * font, no choice here will help.
 */
export function DxfFont({
  lang,
  selected,
  onSelect,
  outlineText,
  onOutlineText,
  missingGlyphs,
}: {
  lang: Lang;
  selected: DxfTextStyle;
  onSelect: (style: DxfTextStyle) => void;
  outlineText: boolean;
  onOutlineText: (on: boolean) => void;
  /** Characters the font had no glyph for, or empty. */
  missingGlyphs: readonly string[];
}): JSX.Element {
  const s = (k: Parameters<typeof t>[0]): string => t(k, lang);

  return (
    <section className="dxf-font">
      <label>
        {s('dxfFontLabel')}
        <select
          data-dxf-font
          value={selected.font}
          onChange={(e) => {
            const next = CJK_FONT_PRESETS.find((p) => p.font === e.target.value);
            if (next) onSelect(next);
          }}
        >
          {CJK_FONT_PRESETS.map((preset) => (
            <option key={preset.font} value={preset.font}>
              {labelFor(preset, lang)}
            </option>
          ))}
        </select>
      </label>
      {/*
        Folded, both of them.

        Eleven lines of prose sat between the download and the diagnostics,
        explaining two expert settings nobody has to touch to finish the job.
        The column's own rule is that what must be read is open and what explains
        is folded — the identity argument and each defect's "what to do" already
        work that way, and these two were the exception. Neither is shortened:
        the `??` question is a real support call and the answer stays whole.
      */}
      <details className="fold" data-fold="dxf-font-why">
        <summary>{s('dxfFontWhy')}</summary>
        <p className="note">{s('dxfFontNote')}</p>
      </details>

      {/*
        The escape hatch from the whole font question, and it is deliberately
        second rather than first. It costs a 1.5 MB download and a much larger
        DXF, and for a reader that HAS the font the plain file is better in every
        way — so it is offered to the operator who has met the problem, described
        by what it does rather than as a fix.
      */}
      <label className="dxf-outline">
        <input
          type="checkbox"
          data-dxf-outline
          checked={outlineText}
          onChange={(e) => onOutlineText(e.target.checked)}
        />
        {s('dxfOutlineLabel')}
      </label>
      <details className="fold" data-fold="dxf-outline-why">
        <summary>{s('dxfOutlineWhy')}</summary>
        <p className="note">{s('dxfOutlineNote')}</p>
      </details>
      {outlineText && missingGlyphs.length > 0 && (
        <p className="note warn" data-outline-missing>
          {fill(s('dxfOutlineMissing'), {
            count: String(missingGlyphs.length),
            characters: missingGlyphs.join(' '),
          })}
        </p>
      )}
    </section>
  );
}

/**
 * The typeface as a person would name it, plus where it is actually installed.
 *
 * The platform is the whole reason for the list, so it belongs in the option
 * rather than in help text nobody opens. The last preset has no typeface — it is
 * the stroke-font route — so it is named by its mechanism instead.
 */
function labelFor(preset: DxfTextStyle, lang: Lang): string {
  const where = t(PLATFORM_KEY[preset.font] ?? 'fontWhereUnknown', lang);
  const name = preset.typeface === '' ? t('fontStrokePair', lang) : preset.typeface;
  return `${name} — ${where}`;
}

const PLATFORM_KEY: Record<string, Parameters<typeof t>[0]> = {
  'msgothic.ttc': 'fontWhereJapaneseWindows',
  'YuGothM.ttc': 'fontWhereWindows10',
  'meiryo.ttc': 'fontWhereWindowsVista',
  'HiraginoSans-W3.ttc': 'fontWhereMacOS',
  'NotoSansCJKjp-Regular.otf': 'fontWhereNoto',
  txt: 'fontWhereOlderAutocad',
};
